import React, { useState, useEffect, useMemo } from "react";
import "./TrainingLearning.scss";
import { Breadcrumb } from "components";
import { useRef } from "react";
import { t } from "react-switch-lang";
import { uploadToBunnyTus } from "./uploadToBunnyTus";
import api from "components/api/api";
import {
  Search,
  Filter,
  Plus,
  X,
  Upload,
  Eye,
  Users,
  Calendar,
  PlayCircle,
  FileText,
  CheckCircle,
  Loader2,
} from "lucide-react";
import { Download, Edit, Trash2 } from "react-feather";
import { getVideoDuration } from "./getVideoDuration";
import toast from "react-hot-toast";
import UserSelector from "pages/Userselector/Userselector";

const TrainingLearning = () => {
  const hasStartedRef = useRef(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedRegion, setSelectedRegion] = useState("all");
  const [exportingOverall, setExportingOverall] = useState(false);
  const [isFileReplaced, setIsFileReplaced] = useState(false);
  const currentProgressRef = useRef({});
  const [isMandatory, setIsMandatory] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [materialsLoading, setMaterialsLoading] = useState(true);
  const [editingMaterial, setEditingMaterial] = useState(null);

  const userRole = (localStorage.getItem("userRole") || "").toLowerCase();
  const userId = localStorage.getItem("userId") || "";
  const iframeRef = useRef(null);
  const progressQueueRef = useRef({});

  const [trainingMaterials, setTrainingMaterials] = useState([]);
  const [videoEngagement, setVideoEngagement] = useState([]);

  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [trainingTitle, setTrainingTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const watchTimerRef = useRef(null);
  const watchedSecondsRef = useRef(0);
  const [mediaData, setMediaData] = useState(null);

  // ── User/region data (for UserSelector + region filter buttons) ───────────
  const [regionData, setRegionData] = useState({});
  const [regionNames, setRegionNames] = useState([]);
  const [selectedLearners, setSelectedLearners] = useState([]);
  // assignSelectedRegion kept only for the payload (read from UserSelector's
  // internal state is not exposed, so we track it separately for submit)
  const [assignSelectedRegion, setAssignSelectedRegion] =
    useState("All Regions");

  const [viewingVideo, setViewingVideo] = useState(null);
  const [currentProgress, setCurrentProgress] = useState({});
  const [videoProcessing, setVideoProcessing] = useState({});
  const [videoProcessingProgress, setVideoProcessingProgress] = useState({});

  const [error, setError] = useState(null);
  const lastMilestoneRef = useRef({});

  const categories = ["all", "video", "document"];
  const isAdmin = userRole === "admin";
  const MILESTONES = [10, 25, 50, 75, 100];

  // ── Helpers ───────────────────────────────────────────────────────────────

  const getAllUsersFromRegionData = (data) => {
    const seen = new Set();
    return Object.values(data)
      .flatMap((region) => Object.values(region || {}))
      .flat()
      .filter((u) => {
        if (seen.has(u.id)) return false;
        seen.add(u.id);
        return true;
      });
  };

  // ── Data fetching ─────────────────────────────────────────────────────────

  const downloadOverallEngagementExcel = async () => {
    setExportingOverall(true);
    try {
      const res = await api.downloadVideoEngagementExcel();
      const url = window.URL.createObjectURL(
        new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `Overall-Training-Engagement-${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download engagement excel", err);
    } finally {
      setExportingOverall(false);
    }
  };

  useEffect(() => {
    if (!isAdmin || trainingMaterials.length === 0) return;
    let intervalId;
    const pollAllVideoStatuses = async () => {
      for (const material of trainingMaterials) {
        if (material.type !== "video") continue;
        const videoId = material.videoPublicId;
        if (
          videoProcessing[videoId] === "ready" ||
          videoProcessing[videoId] === "failed"
        )
          continue;
        try {
          const res = await api.checkBunnyVideoStatus(videoId);
          const { status } = res.data;
          if ([0, 1, 2, 3].includes(status)) {
            setVideoProcessing((prev) => ({
              ...prev,
              [videoId]: "processing",
            }));
          }
          if (status === 4) {
            setVideoProcessing((prev) => ({ ...prev, [videoId]: "ready" }));
          }
          if (status === 5) {
            setVideoProcessing((prev) => ({ ...prev, [videoId]: "failed" }));
          }
        } catch (err) {
          console.warn("Admin polling failed for video:", videoId, err);
        }
      }
    };
    pollAllVideoStatuses();
    intervalId = setInterval(pollAllVideoStatuses, 8000);
    return () => clearInterval(intervalId);
  }, [isAdmin, trainingMaterials]);

  useEffect(() => {
    if (!showUploadModal || !mediaData?.videoPublicId) return;
    let intervalId;
    const pollStatus = async () => {
      try {
        const res = await api.checkBunnyVideoStatus(mediaData.videoPublicId);
        const { status, encodeProgress } = res.data;
        if ([0, 1, 2, 3].includes(status)) {
          setVideoProcessing((prev) => ({
            ...prev,
            [mediaData.videoPublicId]: "processing",
          }));
          setVideoProcessingProgress((prev) => ({
            ...prev,
            [mediaData.videoPublicId]: encodeProgress || 0,
          }));
        }
        if (status === 4) {
          setVideoProcessing((prev) => ({
            ...prev,
            [mediaData.videoPublicId]: "ready",
          }));
          setVideoProcessingProgress((prev) => ({
            ...prev,
            [mediaData.videoPublicId]: 100,
          }));
          clearInterval(intervalId);
        }
        if (status === 5) {
          setVideoProcessing((prev) => ({
            ...prev,
            [mediaData.videoPublicId]: "failed",
          }));
          clearInterval(intervalId);
        }
      } catch (err) {
        console.warn("Bunny status check failed", err);
      }
    };
    pollStatus();
    intervalId = setInterval(pollStatus, 5000);
    return () => clearInterval(intervalId);
  }, [mediaData?.videoPublicId, showUploadModal]);

  useEffect(() => {
    if (showUploadModal && viewingVideo?.videoPublicId) return;
    if (!viewingVideo?.videoPublicId) return;
    let intervalId;
    const pollStatus = async () => {
      try {
        const res = await api.checkBunnyVideoStatus(viewingVideo.videoPublicId);
        const { status } = res.data;
        if ([0, 1, 2, 3].includes(status)) {
          setVideoProcessing((prev) => ({
            ...prev,
            [viewingVideo.videoPublicId]: "processing",
          }));
        }
        if (status === 4) {
          setVideoProcessing((prev) => ({
            ...prev,
            [viewingVideo.videoPublicId]: "ready",
          }));
          clearInterval(intervalId);
        }
        if (status === 5) {
          setVideoProcessing((prev) => ({
            ...prev,
            [viewingVideo.videoPublicId]: "failed",
          }));
          clearInterval(intervalId);
        }
      } catch (err) {
        console.warn("View-time Bunny status check failed", err);
      }
    };
    pollStatus();
    intervalId = setInterval(pollStatus, 5000);
    return () => clearInterval(intervalId);
  }, [viewingVideo?.videoPublicId]);

  const getOriginalFileName = (publicId, format) => {
    if (!publicId) return "";
    return `${publicId}.${format}`;
  };

  const fetchTrainingMaterials = async () => {
    setMaterialsLoading(true);
    try {
      const response = await api.fetchAllTrainings();
      setTrainingMaterials(response.data.data || []);
    } catch (error) {
      console.error("Failed to fetch training materials:", error);
      setError("Failed to load training materials");
    } finally {
      setMaterialsLoading(false);
    }
  };

  const resolveDurationSeconds = (eventDuration, video) => {
    if (typeof eventDuration === "number" && eventDuration > 0)
      return Math.floor(eventDuration);
    if (typeof video?.durationSeconds === "number" && video.durationSeconds > 0)
      return video.durationSeconds;
    return null;
  };

  const updateProgressSafely = (trainingId, progress) => {
    const prev = currentProgressRef.current[trainingId] || 0;
    if (progress < prev) return;
    currentProgressRef.current[trainingId] = progress;
    setCurrentProgress((state) =>
      state[trainingId] === progress
        ? state
        : { ...state, [trainingId]: progress },
    );
    const lastSaved = lastMilestoneRef.current[trainingId] || 0;
    const milestone = MILESTONES.find((m) => progress >= m && lastSaved < m);
    if (milestone) persistProgress(trainingId, milestone);
  };

  useEffect(() => {
    if (
      !viewingVideo ||
      videoProcessing[viewingVideo.videoPublicId] !== "ready"
    )
      return;
    const videoElement = iframeRef.current;
    if (!videoElement) return;

    const handlePlay = () => {
      if (hasStartedRef.current) return;
      hasStartedRef.current = true;
      setIsPlaying(true);
      persistProgress(viewingVideo.id, 1);
    };
    const handleLoadedMetadata = () => {
      const savedProgress = currentProgressRef.current[viewingVideo.id] || 0;
      if (savedProgress && videoElement.duration) {
        videoElement.currentTime =
          (savedProgress / 100) * videoElement.duration;
      }
    };
    const handleTimeUpdate = () => {
      const watched = Math.floor(videoElement.currentTime);
      const duration = Math.floor(videoElement.duration);
      if (!duration) return;
      const progress = Math.floor((watched / duration) * 100);
      updateProgressSafely(viewingVideo.id, progress);
    };
    const handleEnded = () => persistProgress(viewingVideo.id, 100);

    videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);
    videoElement.addEventListener("timeupdate", handleTimeUpdate);
    videoElement.addEventListener("ended", handleEnded);
    videoElement.addEventListener("play", handlePlay);

    return () => {
      videoElement.removeEventListener("loadedmetadata", handleLoadedMetadata);
      videoElement.removeEventListener("timeupdate", handleTimeUpdate);
      videoElement.removeEventListener("ended", handleEnded);
      videoElement.removeEventListener("play", handlePlay);
    };
  }, [viewingVideo?.id, videoProcessing[viewingVideo?.videoPublicId]]);

  const persistProgress = async (trainingId, progress) => {
    if (isAdmin) return;
    if (progressQueueRef.current[trainingId]) return;
    progressQueueRef.current[trainingId] = true;
    try {
      await api.updateTrainingProgress({
        userId,
        trainingId,
        progress,
        status:
          progress === 100
            ? "completed"
            : progress > 0
              ? "in-progress"
              : "not-started",
      });
      lastMilestoneRef.current[trainingId] = progress;
    } catch (err) {
      console.error("Progress update failed", err);
    } finally {
      delete progressQueueRef.current[trainingId];
    }
  };

  useEffect(() => {
    if (!viewingVideo) return;
    const savedProgress = currentProgress[viewingVideo.id] || 0;
    const duration = resolveDurationSeconds(null, viewingVideo);
    if (!duration) return;
    watchedSecondsRef.current = Math.floor((savedProgress / 100) * duration);
    lastMilestoneRef.current[viewingVideo.id] =
      Math.floor(savedProgress / 10) * 10;
  }, [viewingVideo]);

  const playerUrl = viewingVideo
    ? `https://vz-c33d9d7e-d0f.b-cdn.net/${viewingVideo.videoPublicId}/play_720p.mp4`
    : "";

  const fetchUserTrainingMaterials = async () => {
    if (!userId) return;
    setMaterialsLoading(true);
    try {
      const res = await api.fetchUserTrainings(userId);
      const progressMap = {};
      const mappedMaterials = (res.data.data || []).map((item) => {
        progressMap[item.trainingId] = item.progress || 0;
        return {
          id: item.trainingId,
          title: item.title,
          type: item.type,
          videoPublicId: item.videoPublicId,
          documentUrl: item.documentUrl,
          duration: item.duration,
          durationSeconds: item.durationSeconds,
          dueDate: item.dueDate,
          region: "assigned",
          uploadDate: item.createdAt,
          isMandatory: item.isMandatory,
        };
      });
      setTrainingMaterials(mappedMaterials);
      setCurrentProgress(progressMap);
      currentProgressRef.current = progressMap;
    } catch (error) {
      console.error("Failed to fetch user trainings:", error);
      setError("Failed to load assigned trainings");
    } finally {
      setMaterialsLoading(false);
    }
  };

  const processProgress = (watched, duration, trainingId) => {
    const progress = Math.min(100, Math.floor((watched / duration) * 100));
    setCurrentProgress((prev) => ({ ...prev, [trainingId]: progress }));
    const lastSaved = lastMilestoneRef.current[trainingId] || 0;
    const milestone = MILESTONES.find((m) => progress >= m && lastSaved < m);
    if (!milestone) return;
    if (watched < (duration * milestone) / 100 - 5) return;
    persistProgress(trainingId, milestone);
  };

  const fetchLearners = async () => {
    try {
      const res = await api.fetchUsers();
      const users = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data?.users)
            ? res.data.users
            : [];
      const filteredUsers = users.filter((u) => u.role !== "ADMIN");
      const regionMap = { "All Regions": {} };
      filteredUsers.forEach((user) => {
        const { id, username, region, outlet } = user;
        if (!region || !outlet) return;
        if (!regionMap[region]) regionMap[region] = {};
        if (!regionMap[region][outlet]) regionMap[region][outlet] = [];
        // UserSelector expects { id, username } — store username (not name)
        regionMap[region][outlet].push({ id, username, region, outlet });
      });
      setRegionData(regionMap);
      setRegionNames(Object.keys(regionMap));
    } catch (err) {
      console.error("Failed to fetch learners", err);
    }
  };

  // Seed selectedLearners once regionData is ready
  useEffect(() => {
    if (Object.keys(regionData).length === 0) return;
    const allUsers = getAllUsersFromRegionData(regionData);
    setSelectedLearners(allUsers.map((u) => u.id));
  }, [regionData]);

  useEffect(() => {
    const initialize = async () => {
      if (isAdmin) {
        await fetchTrainingMaterials();
        await fetchLearners();
      } else {
        await fetchUserTrainingMaterials();
      }
    };
    initialize();
  }, [isAdmin, userId]);

  useEffect(() => {
    const defaultDue = new Date();
    defaultDue.setDate(defaultDue.getDate() + 7);
    setDueDate(defaultDue.toISOString().split("T")[0]);
  }, []);

  // ── Edit ──────────────────────────────────────────────────────────────────

  const handleEditTraining = async (material) => {
    try {
      setLoading(true);
      const res = await api.fetchTrainingById(material.id);
      const data = res.data.data;
      setEditingMaterial(data);
      setTrainingTitle(data.title || "");
      setDueDate(
        data.dueDate
          ? data.dueDate.split("T")[0]
          : new Date(data.uploadDate).toISOString().split("T")[0],
      );
      setAssignSelectedRegion(
        data.region === "all"
          ? "All Regions"
          : data.region.charAt(0).toUpperCase() + data.region.slice(1),
      );
      setSelectedLearners(data.assignedUserIds || []);
      if (data.type === "video") {
        setMediaData({
          type: "video",
          videoPublicId: data.videoPublicId,
          videoCloudResourceType: "video",
          videoFormat: data.videoFormat,
          duration: data.duration,
        });
      } else {
        setMediaData({ type: "document", documentUrl: data.documentUrl });
      }
      setIsMandatory(Boolean(data.isMandatory));
      setShowUploadModal(true);
    } catch (err) {
      console.error("Failed to fetch training", err);
      toast.error("Failed to load training for edit");
    } finally {
      setLoading(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDeleteTraining = async (materialId) => {
    if (!window.confirm("Are you sure you want to delete this training?"))
      return;
    try {
      await api.deleteTraining(materialId);
      if (isAdmin) {
        await fetchTrainingMaterials();
      } else {
        await fetchUserTrainingMaterials();
      }
      toast.success("Training deleted successfully");
    } catch (err) {
      console.error("Delete failed", err);
      toast.error("Failed to delete training");
    }
  };

  // ── File upload ───────────────────────────────────────────────────────────

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!trainingTitle.trim()) {
      toast("Please enter training title first", { icon: "ℹ️" });
      e.target.value = "";
      return;
    }
    setUploadedFile(file);
    setIsFileReplaced(true);
    const isVideo = file.type.startsWith("video/");
    const ext = file.name.toLowerCase().split(".").pop();
    const isDocument = [
      "pdf",
      "doc",
      "docx",
      "xls",
      "xlsx",
      "xlsm",
      "xlsb",
      "csv",
      "ppt",
      "pptx",
    ].includes(ext);
    setIsUploading(true);
    setUploadProgress(0);
    try {
      if (isVideo) {
        const durationSeconds = await getVideoDuration(file);
        const formattedDuration =
          durationSeconds < 60
            ? `${durationSeconds} sec`
            : `${Math.floor(durationSeconds / 60)} min ${durationSeconds % 60} sec`;
        const createRes = await api.createBunnyVideo(trainingTitle);
        const { videoId } = createRes.data;
        const sigRes = await api.getTusSignature(videoId);
        const { signature, expires, libraryId } = sigRes.data;
        const result = await uploadToBunnyTus({
          file,
          videoId,
          libraryId,
          signature,
          expires,
          title: trainingTitle,
          onProgress: setUploadProgress,
        });
        setMediaData({
          ...result,
          type: "video",
          duration: formattedDuration,
          durationSeconds,
        });
      } else if (isDocument) {
        const formData = new FormData();
        formData.append("file", file);
        const res = await api.uploadDocument(formData);
        setMediaData({
          type: "document",
          documentUrl: res.data.documentUrl,
          format: file.name.split(".").pop(),
        });
      } else {
        toast.error("Unsupported file type");
      }
    } catch (err) {
      console.error(err);
      toast.error("Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!trainingTitle.trim()) return toast("Enter title");
    if (!editingMaterial && !mediaData)
      return toast("Upload a file", { icon: "ℹ️" });
    if (selectedLearners.length === 0)
      return toast("Select learners", { icon: "ℹ️" });
    setLoading(true);
    try {
      const videoPlaybackUrl = `https://vz-c33d9d7e-d0f.b-cdn.net/${mediaData.videoPublicId}/play_720p.mp4`;
      const payload = {
        material: {
          title: trainingTitle,
          region:
            assignSelectedRegion === "All Regions"
              ? "all"
              : assignSelectedRegion.toLowerCase(),
          type: mediaData.type,
          duration: mediaData.type === "video" ? mediaData.duration : null,
          durationSeconds:
            mediaData.type === "video" ? mediaData.durationSeconds : null,
          isMandatory: isMandatory,
        },
        ...(mediaData.type === "video" && {
          videoPublicId: mediaData.videoPublicId,
          videoPlaybackUrl: videoPlaybackUrl,
          videoFormat: mediaData.videoFormat,
        }),
        ...(mediaData.type === "document" && {
          documentUrl: mediaData.documentUrl,
        }),
        userIds: selectedLearners,
        dueDate: new Date(dueDate).toISOString(),
      };
      if (editingMaterial) {
        await api.updateTraining(editingMaterial.id, payload);
        toast.success("Training updated successfully");
      } else {
        await api.uploadTraining(payload);
        toast.success("Training uploaded successfully");
      }
      await fetchTrainingMaterials();
      resetModal();
      setEditingMaterial(null);
      setIsMandatory(false);
    } catch (err) {
      console.error(err);
      toast.error("Operation failed");
    } finally {
      setLoading(false);
    }
  };

  // ── View / close ──────────────────────────────────────────────────────────

  const updateProgressInBackend = async (trainingId, progress) => {
    if (isAdmin) return;
    try {
      await api.updateTrainingProgress({
        userId,
        trainingId,
        progress,
        status:
          progress >= 100
            ? "completed"
            : progress > 0
              ? "in-progress"
              : "not-started",
      });
    } catch (error) {
      console.error("Failed to update progress:", error);
    }
  };

  const viewMaterial = (material) => {
    if (material.type === "video") {
      if (!isAdmin) {
        setViewingVideo(material);
        return;
      }
      window.open(
        `https://vz-c33d9d7e-d0f.b-cdn.net/${material.videoPublicId}/play_720p.mp4`,
        "_blank",
      );
    } else {
      const current = currentProgress[material.id] || 0;
      if (current < 100) {
        updateProgressInBackend(material.id, 100);
        setCurrentProgress((prev) => ({ ...prev, [material.id]: 100 }));
      }
      window.open(material.documentUrl, "_blank");
    }
  };

  const closeVideoModal = () => {
    hasStartedRef.current = false;
    if (viewingVideo?.id) delete lastMilestoneRef.current[viewingVideo.id];
    clearInterval(watchTimerRef.current);
    watchedSecondsRef.current = 0;
    setIsPlaying(false);
    setViewingVideo(null);
  };

  const resetModal = () => {
    setShowUploadModal(false);
    setTrainingTitle("");
    setUploadedFile(null);
    setUploadProgress(0);
    setMediaData(null);
    setIsFileReplaced(false);
    setError(null);
    setIsUploading(false);
    setEditingMaterial(null);
    setAssignSelectedRegion("All Regions");
    setIsMandatory(false);
    // Re-select all users
    const allUsers = getAllUsersFromRegionData(regionData);
    setSelectedLearners(allUsers.map((u) => u.id));
  };

  // ── Filtering ─────────────────────────────────────────────────────────────

  const getMaterialProgress = (materialId) => {
    if (!isAdmin) return currentProgress[materialId] || 0;
    const engagements = videoEngagement.filter(
      (e) => e.trainingId === materialId,
    );
    if (engagements.length === 0) return 0;
    const avg =
      engagements.reduce((sum, e) => sum + (e.progress || 0), 0) /
      engagements.length;
    return Math.round(avg);
  };

  const filteredMaterials = useMemo(() => {
    return trainingMaterials.filter((material) => {
      const matchesSearch =
        material.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        material.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        selectedCategory === "all" ||
        material.type === selectedCategory.toLowerCase();
      const matchesRegion =
        selectedRegion === "all"
          ? true
          : material.region === selectedRegion.toLowerCase() ||
            material.region === "all";
      return matchesSearch && matchesCategory && matchesRegion;
    });
  }, [searchQuery, selectedCategory, selectedRegion, trainingMaterials]);

  const getStats = () => {
    const totalMaterials = trainingMaterials.length;
    const totalAssignments = trainingMaterials.reduce(
      (sum, m) => sum + (m.assignedTo || 0),
      0,
    );
    const completedVideos = videoEngagement.filter(
      (e) => e.status === "completed",
    ).length;
    const avgCompletion = Math.round(
      videoEngagement.reduce((sum, e) => sum + e.progress, 0) /
        (videoEngagement.length || 1),
    );
    return { totalMaterials, totalAssignments, completedVideos, avgCompletion };
  };

  const stats = getStats();

  // ── Loading state ─────────────────────────────────────────────────────────

  if (materialsLoading) {
    return (
      <div className="training-learning">
        <Breadcrumb title={t("Training & Learning")} />
        <div style={{ textAlign: "center", padding: "3rem" }}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="training-learning offers-container">
      <Breadcrumb title={t("Training & Learning")} />

      <div className="offers-header">
        <div className="header-content">
          <div className="header-title-row">
            <div>
              <p className="page-subtitle">
                {isAdmin
                  ? "Upload, Assign & Track Learning Materials"
                  : "Access your training materials and track progress"}
              </p>
            </div>
            {isAdmin && (
              <button
                className="create-btn"
                onClick={() => setShowUploadModal(true)}
              >
                <Plus size={20} />
                Upload
              </button>
            )}
          </div>
        </div>

        <div className="header-stats">
          <div className="stat-card">
            <span className="stat-value">{stats.totalMaterials}</span>
            <span className="stat-label">Training Materials</span>
          </div>
          {isAdmin && (
            <>
              <div className="stat-card">
                <span className="stat-value">{stats.totalAssignments}</span>
                <span className="stat-label">Total Assignments</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{stats.completedVideos}</span>
                <span className="stat-label">Completed</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{stats.avgCompletion}%</span>
                <span className="stat-label">Avg Completion</span>
              </div>
            </>
          )}
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: "1rem",
            margin: "1rem 0",
            backgroundColor: "#fee",
            color: "#c33",
            borderRadius: "4px",
          }}
        >
          {error}
        </div>
      )}

      {/* ── Filters ── */}
      <div className="filters-section">
        <div className="search-box">
          <Search className="search-icon" size={20} />
          <input
            type="text"
            placeholder="Search training materials..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <div className="filter-label">
            <Filter size={16} />
            <span>Type</span>
          </div>
          <div className="filter-buttons">
            {categories.map((category) => (
              <button
                key={category}
                className={`filter-btn ${selectedCategory === category ? "active" : ""}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category === "all" ? "All" : category}
              </button>
            ))}
          </div>
        </div>

        {isAdmin && (
          <div className="filter-group">
            <div className="filter-label">
              <Filter size={16} />
              <span>Region</span>
            </div>
            <div className="filter-buttons">
              <button
                className={`filter-btn ${selectedRegion === "all" ? "active" : ""}`}
                onClick={() => setSelectedRegion("all")}
              >
                All Regions
              </button>
              {regionNames
                .filter((r) => r !== "All Regions")
                .map((region) => (
                  <button
                    key={region}
                    className={`filter-btn ${selectedRegion === region ? "active" : ""}`}
                    onClick={() => setSelectedRegion(region)}
                  >
                    {region}
                  </button>
                ))}
            </div>
            <div className="filter-action-cont">
              <button
                className="export-btn"
                onClick={() => {
                  setSearchQuery("");
                  setSelectedCategory("all");
                  setSelectedRegion("all");
                }}
              >
                Clear Filters
              </button>
              <button
                className="export-btn"
                onClick={downloadOverallEngagementExcel}
                disabled={exportingOverall}
              >
                {exportingOverall ? (
                  <>
                    <Loader2 size={16} className="spinner-offer" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    Export Report
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Training Grid ── */}
      <div className="offers-grid">
        {filteredMaterials.length > 0 ? (
          filteredMaterials.map((material) => (
            <div
              key={material.id}
              className={`offer-card ${material.isMandatory ? "mandatory" : ""}`}
            >
              <div className="offer-header-badge">
                <div className="category-badge">
                  {material.type === "video" ? (
                    <PlayCircle size={18} />
                  ) : (
                    <FileText size={18} />
                  )}
                  <span>{material.type}</span>
                </div>
                <div className="region-badge">
                  {!isAdmin && getMaterialProgress(material.id) >= 100
                    ? "Completed"
                    : material.region}
                </div>
                {material.isMandatory && (
                  <span className="mandatory-badge">Mandatory</span>
                )}
              </div>

              <h3 className="offer-title">{material.title}</h3>
              {material.description && (
                <p className="offer-description">{material.description}</p>
              )}

              {isAdmin ? (
                <div className="admin-meta">
                  <div className="meta-item">
                    <Users size={14} />
                    <span>{material.assignedTo || 0} assigned</span>
                  </div>
                  <div className="meta-item">
                    <span>{material.completionRate || 0}% completed</span>
                  </div>
                </div>
              ) : (
                material.type === "video" && (
                  <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "0.5rem",
                        fontSize: "0.85rem",
                        color: "#7f8c8d",
                      }}
                    >
                      <span>Progress</span>
                      <span style={{ fontWeight: "600", color: "#2c3e50" }}>
                        {getMaterialProgress(material.id)}%
                      </span>
                    </div>
                    <div
                      style={{
                        height: "8px",
                        backgroundColor: "#e9ecef",
                        borderRadius: "10px",
                        overflow: "hidden",
                        boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          width: `${getMaterialProgress(material.id)}%`,
                          backgroundColor:
                            getMaterialProgress(material.id) >= 100
                              ? "#2ecc71"
                              : getMaterialProgress(material.id) >= 50
                                ? "#3498db"
                                : "#f39c12",
                          borderRadius: "10px",
                          transition:
                            "width 0.3s ease, background-color 0.3s ease",
                          boxShadow: "0 0 10px rgba(52,152,219,0.5)",
                        }}
                      />
                    </div>
                  </div>
                )
              )}

              <div className="offer-footer">
                <div className="offer-meta">
                  <Calendar size={14} />
                  <span>
                    {material.dueDate
                      ? new Date(material.dueDate).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })
                      : "N/A"}
                  </span>
                </div>
                {material.type === "video" && (
                  <div className="valid-until">⏱️ {material.duration}</div>
                )}
              </div>

              <button
                className="view-details-btn"
                onClick={() => viewMaterial(material)}
                disabled={
                  isAdmin &&
                  material.type === "video" &&
                  videoProcessing[material.videoPublicId] === "processing"
                }
                style={{
                  opacity:
                    isAdmin &&
                    material.type === "video" &&
                    videoProcessing[material.videoPublicId] === "processing"
                      ? 0.6
                      : 1,
                  cursor:
                    isAdmin &&
                    material.type === "video" &&
                    videoProcessing[material.videoPublicId] === "processing"
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                <Eye size={18} />
                View Material
              </button>

              {isAdmin && (
                <div className="admin-actions">
                  <button
                    className="icon-btn edit"
                    onClick={() => handleEditTraining(material)}
                    title="Edit"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    className="icon-btn delete"
                    onClick={() => handleDeleteTraining(material.id)}
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="no-results">
            <Search size={48} />
            <h3>No training materials found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* ── Video Viewing Modal (non-admin) ── */}
      {viewingVideo && !isAdmin && (
        <div className="modal-overlay" onClick={closeVideoModal}>
          <div
            className="modal-content-training"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "1000px" }}
          >
            <div className="modal-header-offer">
              <h2>{viewingVideo.title}</h2>
              <button className="close-btn" onClick={closeVideoModal}>
                <X size={24} />
              </button>
            </div>

            <div className="modal-body">
              {videoProcessing[viewingVideo.videoPublicId] === "processing" && (
                <div
                  style={{
                    padding: "1.5rem",
                    textAlign: "center",
                    background: "#fff3cd",
                    border: "1px solid #ffeeba",
                    borderRadius: "8px",
                    color: "#856404",
                    fontWeight: 600,
                  }}
                >
                  ⏳ Video is still processing. Please wait…
                </div>
              )}

              {videoProcessing[viewingVideo.videoPublicId] === "ready" && (
                <video
                  ref={iframeRef}
                  src={playerUrl}
                  controls
                  style={{
                    width: "100%",
                    height: "70vh",
                    borderRadius: "8px",
                    backgroundColor: "#000",
                    objectFit: "contain",
                  }}
                />
              )}

              {videoProcessing[viewingVideo.videoPublicId] === "failed" && (
                <div
                  style={{
                    padding: "1.5rem",
                    textAlign: "center",
                    background: "#f8d7da",
                    border: "1px solid #f5c6cb",
                    borderRadius: "8px",
                    color: "#721c24",
                    fontWeight: 600,
                  }}
                >
                  ❌ Video processing failed. Please contact support.
                </div>
              )}

              {currentProgress[viewingVideo.id] >= 100 && (
                <div
                  style={{
                    marginTop: "1rem",
                    padding: "1rem",
                    backgroundColor: "#d4edda",
                    border: "1px solid #c3e6cb",
                    borderRadius: "8px",
                    color: "#155724",
                    textAlign: "center",
                    fontWeight: "600",
                  }}
                >
                  🎉 Congratulations! You've completed this training video!
                </div>
              )}

              {viewingVideo.isMandatory && (
                <div
                  className="mandatory-item"
                  style={{
                    marginTop: "1rem",
                    padding: "0.75rem 1rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.25rem",
                  }}
                >
                  <label
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "#dc2626",
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                    }}
                  >
                    Mandatory
                  </label>
                  <span style={{ color: "#dc2626", fontWeight: 700 }}>
                    Yes — Required viewing
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Upload & Assign Modal ── */}
      {showUploadModal && isAdmin && (
        <div className="modal-overlay" onClick={resetModal}>
          <div
            className="modal-content-training"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "800px" }}
          >
            <div className="modal-header-offer">
              <h2>
                {editingMaterial ? "Edit Training" : "Upload & Assign Training"}
              </h2>
              <button className="close-btn" onClick={resetModal}>
                <X size={24} />
              </button>
            </div>

            <div className="modal-body">
              {/* ── Title ── */}
              <div className="form-section">
                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Training Title *</label>
                    <input
                      type="text"
                      value={trainingTitle}
                      onChange={(e) => setTrainingTitle(e.target.value)}
                      placeholder="Enter training title"
                    />
                  </div>
                </div>

                {/* ── File upload area ── */}
                <div className="upload-area" style={{ marginTop: "1rem" }}>
                  <div className="upload-icon">
                    {mediaData ? (
                      <CheckCircle size={48} color="#2ecc71" />
                    ) : (
                      "📁"
                    )}
                  </div>
                  <p>
                    {uploadedFile && isFileReplaced && (
                      <>
                        Selected: <strong>{uploadedFile.name}</strong>
                      </>
                    )}
                    {!uploadedFile && editingMaterial && mediaData && (
                      <>
                        Current file:{" "}
                        <strong>
                          {getOriginalFileName(
                            mediaData.videoPublicId,
                            mediaData.videoFormat,
                          )}
                        </strong>
                        <br />
                        <span style={{ fontSize: "0.85rem", color: "#7f8c8d" }}>
                          Upload a new file only if you want to replace it
                        </span>
                      </>
                    )}
                    {!uploadedFile && !editingMaterial && (
                      <>Drag & drop files here or click to browse</>
                    )}
                  </p>
                  {mediaData && (
                    <p
                      style={{
                        color: "#2ecc71",
                        fontWeight: "600",
                        marginTop: "0.5rem",
                      }}
                    >
                      ✓ File uploaded successfully
                    </p>
                  )}
                  <p
                    style={{
                      fontSize: "0.85rem",
                      color: "#7f8c8d",
                      marginTop: "0.5rem",
                    }}
                  >
                    Supported: Videos (MP4, WebM, MOV), Documents (PDF, DOC,
                    PPT, XLS)
                  </p>
                  <label
                    className="btn-primary"
                    style={{
                      cursor: isUploading ? "not-allowed" : "pointer",
                      marginTop: "1rem",
                    }}
                  >
                    <Upload size={18} />
                    {isUploading ? "Uploading..." : "Select File"}
                    <input
                      type="file"
                      style={{ display: "none" }}
                      onChange={handleFileSelect}
                      accept="video/*,.pdf,.doc,.docx,.xls,.xlsx,.xlsm,.xlsb,.csv,.ppt,.pptx"
                      disabled={isUploading}
                    />
                  </label>
                  {uploadProgress > 0 && isUploading && (
                    <div className="progress-bar" style={{ marginTop: "1rem" }}>
                      <div
                        className="progress-fill"
                        style={{ width: `${uploadProgress}%` }}
                      />
                      <span className="progress-text">{uploadProgress}%</span>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Due date ── */}
              <div className="form-group" style={{ marginTop: "1rem" }}>
                <label>Due Date *</label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>

              {/* ── Mandatory toggle ── */}
              <div
                className="form-group mandatory-toggle"
                style={{ marginTop: "0.75rem" }}
              >
                <input
                  type="checkbox"
                  id="mandatory-training-check"
                  checked={isMandatory}
                  onChange={(e) => setIsMandatory(e.target.checked)}
                />
                <label htmlFor="mandatory-training-check">
                  Mandatory Training
                </label>
              </div>

              {/* ── UserSelector replaces old region/outlet/search/user list ── */}
              <div className="form-section" style={{ marginTop: "1rem" }}>
                <h3 className="section-title">Assign Learners</h3>
                <UserSelector
                  regionData={regionData}
                  selectedUsers={selectedLearners}
                  onChange={setSelectedLearners}
                />
              </div>

              {/* ── Footer ── */}
              <div className="modal-footer" style={{ marginTop: "2rem" }}>
                <button
                  className="cancel-btn"
                  onClick={resetModal}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  className="save-btn"
                  onClick={handleSubmit}
                  disabled={
                    !trainingTitle.trim() ||
                    selectedLearners.length === 0 ||
                    !dueDate ||
                    loading ||
                    isUploading
                  }
                  style={{
                    opacity:
                      !trainingTitle.trim() ||
                      selectedLearners.length === 0 ||
                      !dueDate ||
                      loading ||
                      isUploading
                        ? 0.6
                        : 1,
                    backgroundColor: "#2ecc71",
                  }}
                >
                  <CheckCircle size={18} />
                  {loading
                    ? "Submitting..."
                    : `Upload & Assign to ${selectedLearners.length} Learner(s)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingLearning;
